<template>
  <div class="detail" v-if="currentTrack">
    <div class="detail-wrapper">
      <h3 class="detail-title">Basic</h3>

      <div class="detail-row detail-row--3cols">
        <div :class="`detail-cell${getError('title') ? ' has-error' : ''}`" style="margin-top: -9px;">
          <span class="detail-label">Title</span>
          <TextInput
            :originalValue="currentTrack.title"
            ref="title"
            :readOnly="false"
            required
            @onFocus="onFocus('title')"
            @onBlur="onBlur"
            @onValueChanged="updateTrackField([currentTrack],'title',$event)"
          ></TextInput>
          <span class="detail-error">This field is required</span>
        </div>

        <div v-if="['MUSIC'].includes(fileType)" :class="`detail-cell${getError('artists') ? ' has-error' : ''}`">
          <span class="detail-label">Artist(s)</span>
          <ArtistBadgeList
            :artists="currentTrack.artists"
            :readOnly="false"
            required
            @onFocus="onFocus('artists')"
            @onBlur="onBlur"
            @click.prevent="openParameterSelector('showArtistSelector','modalArtists', [currentTrack])"
            placeholder
          />
          <span class="detail-error">This field is required</span>
        </div>

        <div v-if="['JINGLE', 'PODCAST'].includes(fileType)" :class="`detail-cell${getError('artist') ? ' has-error' : ''}`">
          <span class="detail-label">Author</span>
          <TextInput
            :originalValue="currentTrack.artist"
            ref="artist"
            :readOnly="false"
            @onFocus="onFocus('artist')"
            @onBlur="onBlur"
            @onValueChanged="updateTrackField([currentTrack],'artist',$event)"
          ></TextInput>
          <span class="detail-error">This field is required</span>
        </div>
        <div :class="`detail-cell${getError('isExplicit') ? ' has-error' : ''}`">
          <span class="detail-label">Explicit</span>
          <ToggleInput
                    :originalValue="currentTrack.isExplicit"
                    :id="`tracklist-toggle-${currentTrack.uid}`"
                    @onValueChanged="updateTrackField([currentTrack],'isExplicit',$event)"
          />
        </div>
      </div>

      <div class="detail-row detail-row--2cols">
        <div :class="`detail-cell${getError('year') ? ' has-error' : ''}`">
          <span class="detail-label">Year</span>
          <NumberInput
            :originalValue="currentTrack.year"
            ref="year"
            :readOnly="false"
            :min="1900"
            :max="maxYear"
            @onFocus="onFocus('year')"
            @onBlur="onBlur"
            @onValueChanged="updateTrackField([currentTrack],'year',$event)"
            class="year"
            placeholder
          />
          <span class="detail-error">This field is required and must be a valid year between 1900 and {{ maxYear }}</span>
        </div>
  
        <div v-if="['MUSIC'].includes(fileType)" :class="`detail-cell${getError('styles') ? ' has-error' : ''}`">
          <span class="detail-label">Style(s)</span>
          <StyleBadgeList
            :styles="currentTrack.styles"
            ref="styles"
            :readOnly="true"
            placeholder
            @onFocus="onFocus('styles')"
            @onBlur="onBlur"
            @click.prevent="openParameterSelector('showStyleSelector','modalStyles', [currentTrack])"
          />
          <span class="detail-error">This field is required</span>
        </div>
        <div class="detail-cell">
          <span class="detail-label">Instrument(s)</span>
          <!--          {{currentTrack.instruments}}-->
          <InstrumentsBadgeList
              :instruments="currentTrack.instruments"
              ref="instruments"
              :readOnly="false"
              placeholder
              @onFocus="onFocus('instruments')"
              @onBlur="onBlur"
              @click.prevent="openParameterSelector('showInstrumentsSelector','modalInstruments', [currentTrack])"
          />
        </div>
      </div>

      <div class="detail-row detail-row--2cols">
        <div :class="`detail-cell${getError('tags') ? ' has-error' : ''}`">
          <span class="detail-label">Tag(s)</span>
<!--          {{currentTrack.tags}}-->
          <TagsBadgeList
            :tags="currentTrack.tags"
            ref="tags"
            :readOnly="false"
            placeholder
            @onFocus="onFocus('tags')"
            @onBlur="onBlur"
            @click.prevent="openParameterSelector('showTagsSelector','modalTags', [currentTrack])"
          />
          <span class="detail-error">This field is required</span>
        </div>

        <div :class="`detail-cell${getError('bpm') ? ' has-error' : ''}`">
          <span class="detail-label">BPM</span>
          <NumberInput
            :originalValue="currentTrack.bpm"
            ref="bpm"
            :readOnly="false"
            placeholder="Bpm"
            @onFocus="onFocus('bpm')"
            @onBlur="onBlur"
            @onValueChanged="updateTrackField([currentTrack],'bpm',$event)"
            :min="0"
            :max="300"
            class="bpm"
          />
          <span class="detail-error">This field is required</span>
        </div>
        <div :class="`detail-cell${getError('composer') ? ' has-error' : ''}`" style="margin-top: -8px;">
          <span class="detail-label">Composer</span>
          <input
              type="text"
              ref="search"
              maxlength="250"
              v-model="currentTrack.composer"
              class="input"
              style="margin-top: 8px;"
          />
          <span class="detail-error">This field is required</span>
        </div>
      </div>

      <div class="detail-row detail-row--2cols">
        <div class="detail-cell">
          <span class="detail-label">Infos</span>
          <textarea
            ref="description"
            class="textarea"
            placeholder
            rows="4"
            :readonly="false"
            v-model="currentTrack.description"
            @onFocus="onFocus('description')"
            @onBlur="onBlur"
            @change="updateTrackField([currentTrack],'description', currentTrack.description)"></textarea>
        </div>
        <div class="detail-cell">
          <span class="detail-label">Vocal instrument(s)</span>
          <!--          {{currentTrack.instruments}}-->
          <VocalInstrumentsBadgeList
              :vocalInstruments="currentTrack.vocalInstruments"
              ref="vocalInstruments"
              :readOnly="false"
              placeholder
              @onFocus="onFocus('vocalInstruments')"
              @onBlur="onBlur"
              @click.prevent="openParameterSelector('showVocalInstrumentsSelector','modalVocalInstruments', [currentTrack])"
          />
        </div>
        <div class="detail-cell">
          <span class="detail-label">Length</span>
          <span class="detail-value">{{ getLength }}</span>
        </div>
      </div>

      <h3 class="detail-title">Advanced</h3>

      <div class="detail-row detail-row--2cols">
        <div :class="`detail-cell${getError('labelId') ? ' has-error' : ''}`">
          <span class="detail-label">Label</span>
          <LabelTextInput
            :readOnly="true"
            :label="currentTrack.label"
            ref="label"
            placeholder
            @onFocus="onFocus('label')"
            @onBlur="onBlur"
            @click.prevent="openParameterSelector('showLabelSelector','modalLabels', [currentTrack])"
          />
          <span class="detail-error">This field is required</span>
        </div>

        <div :class="`detail-cell${getError('languages') ? ' has-error' : ''}`">
          <span class="detail-label">Language(s)</span>
          <LanguageBadgeList
            :languages="currentTrack.languages"
            ref="languages"
            placeholder
            :readOnly="false"
            @onFocus="onFocus('languages')"
            @onBlur="onBlur"
            @click.prevent="openParameterSelector('showLanguageSelector','modalLanguages', [currentTrack])"
          />
          <span class="detail-error">This field is required</span>
        </div>
      </div>

      <div v-if="['MUSIC', 'JINGLE','PODCAST'].includes(fileType)" class="detail-row detail-row--3cols">

        <div :class="`detail-cell${getError('energy') ? ' has-error' : ''}`">
          <span class="detail-label">Energy</span>
          <MultiChoiceTextInput
            :readOnly="false"
            ref="energy"
            :originalValue="currentTrack.energy"
            :enumValues="Constants.energies"
            @onValueChanged="updateTrackField([currentTrack],'energy',$event)"
          />
          <span class="detail-error">This field is required</span>
        </div>

        <div :class="`detail-cell${getError('mood') ? ' has-error' : ''}`">
          <span class="detail-label">Mood</span>
          <MultiChoiceTextInput
            :readOnly="false"
            :originalValue="currentTrack.mood"
            :enumValues="Constants.moods"
            @onValueChanged="updateTrackField([currentTrack],'mood',$event)"
          />
          <span class="detail-error">This field is required</span>
        </div>
        
        <div v-if="['MUSIC', 'JINGLE'].includes(fileType)" :class="`detail-cell${getError('genderId') ? ' has-error' : ''}`">
          <span class="detail-label">Gender</span>
          <MultiChoiceTextInput
            :readOnly="false"
            :originalValue="currentTrack.genderId"
            :enumValues="Constants.genders"
            @onValueChanged="updateTrackField([currentTrack],'genderId',$event)"
          />
          <span class="detail-error">This field is required</span>
        </div>
      </div>
      
      <!-- @TODO: enable it when feature it becomes available -->
<!--        <div class="detail-cell detail-cell&#45;&#45;big">-->
<!--          <span class="detail-label">Use as demo track for mixing (Cue Points)</span>-->
<!--          <span class="toggle">-->
<!--            <label for="editMetaLabel">No</label>-->
<!--            <input-->
<!--              type="checkbox"-->
<!--              class="toggle-input"-->
<!--              id="editMetaLabel"-->
<!--              v-model="currentTrack.isMixDemo"-->
<!--            />-->
<!--            <label for="editMetaLabel" class="toggle-label"></label>-->
<!--            <label for="editMetaLabel">Yes</label>-->
<!--          </span>-->
<!--        </div>-->

      <div class="detail-footer">
        <button
          v-if="$store.state.tracks.tracksBucketIndex !== 0"
          class="detail-prev empty"
          :disabled="viewState.isUpdatingTrack"
          v-on:click="onPrev"
        >Prev</button>
        <div class="detail-footer-right">
          <div v-if="viewState.isUpdatingTrack" class="detail-footer-loader">Saving...</div>
          <button
            v-if="!$store.state.tracks.closeAfterEdit"
            class="detail-save"
            v-on:click="onSave"
          >Save</button>

          <button
            v-else
            class="detail-save"
            :disabled="isDisable"
            v-on:click="onSave"
          >{{ $store.state.tracks.tracksBucketIndex === $store.state.tracks.tracksBucket.length - 1 ? 'Save & close' : 'Save &amp; next' }}</button>
        </div>
      </div>
    </div>

    <SelectorArtist
      v-if="['MUSIC'].includes(fileType)"
      v-show="viewState.showArtistSelector"
      ref="modalArtists"
      :stationUid="currentStationUid"
      :noModalEdit="true"
      @newValue="onNewValue"
      @onHidden="resetModalState('showArtistSelector')"
    />
    <SelectorInstruments
        v-show="viewState.showInstrumentSelector"
        ref="modalInstruments"
        :stationUid="currentStationUid"
        :noModalEdit="true"
        @newValueInstruments="onNewValue"
        @onHidden="resetModalState('showInstrumentSelector')"
    />
    <SelectorVocalInstruments
        v-show="viewState.showVocalInstrumentSelector"
        ref="modalVocalInstruments"
        :stationUid="currentStationUid"
        :noModalEdit="true"
        @newValueVocalInstruments="onNewValue"
        @onHidden="resetModalState('showVocalInstrumentSelector')"
    />
    <SelectorStyle
      v-show="viewState.showStyleSelector"
      ref="modalStyles"
      :stationUid="currentStationUid"
      :noModalEdit="true"
      @newValue="onNewValue"
      @onHidden="resetModalState('showStyleSelector')"
    />
    <SelectorLanguages
      v-show="viewState.showLanguageSelector"
      ref="modalLanguages"
      :stationUid="currentStationUid"
      :noModalEdit="true"
      @newValue="onNewValue"
      @onHidden="resetModalState('showLanguageSelector')"
    />
    <SelectorLabels
      v-show="viewState.showLabelSelector"
      ref="modalLabels"
      :multiselect="false"
      :stationUid="currentStationUid"
      :noModalEdit="true"
      @newValue="onNewValue"
      @onHidden="resetModalState('showLabelSelector')"
    />
    <SelectorTags
      v-show="viewState.showTagsSelector"
      ref="modalTags"
      :stationUid="currentStationUid"
      :noModalEdit="true"
      @newValue="onNewValue"
      @onHidden="resetModalState('showTagsSelector')"
    />
  </div>
</template>


<script>
import { HTTP } from "@/utils/http";
import Apis from "@/apis/index";
import { mapState, mapActions } from "vuex";
import Constants from "@/store/constants.js";
// Inputs
import TagsBadgeList from "@/components/common/TagsBadgeList.vue";
import StyleBadgeList from "@/components/common/StyleBadgeList.vue";
import ArtistBadgeList from "@/components/common/ArtistBadgeList.vue";
import LanguageBadgeList from "@/components/common/LanguageBadgeList.vue";
import TextInput from "@/components/common/TextInput.vue";
import DurationText from "@/components/common/DurationText.vue";
import NumberInput from "@/components/common/NumberInput.vue";
import LabelTextInput from "@/components/common/LabelTextInput.vue";
import MultiChoiceTextInput from "@/components/common/MultiChoiceTextInput.vue";
import ToggleInput from "@/components/common/ToggleInput.vue";
// Selectors
import SelectorArtist from "@/components/Library/SelectorArtist.vue";
import SelectorStyle from "@/components/Library/SelectorStyle.vue";
import SelectorLanguages from "@/components/Library/SelectorLanguages.vue";
import SelectorLabels from "@/components/Library/SelectorLabels.vue";
import SelectorTags from "@/components/Library/SelectorTags.vue";
import SelectorInstruments from "@/components/Boxes/SelectorInstrument";
import SelectorVocalInstruments from "@/components/Boxes/SelectorVocalInstrument";
import InstrumentsBadgeList from "@/components/common/InstrumentsBadgeList";
import VocalInstrumentsBadgeList from "@/components/common/VocalInstrumentsBadgeList";

export default {
  name: "TrackDetails",
  components: {
    SelectorInstruments,
    SelectorVocalInstruments,
    SelectorTags,
    SelectorArtist,
    SelectorStyle,
    SelectorLanguages,
    SelectorLabels,
    TagsBadgeList,
    InstrumentsBadgeList,
    VocalInstrumentsBadgeList,
    ArtistBadgeList,
    StyleBadgeList,
    LanguageBadgeList,
    TextInput,
    MultiChoiceTextInput,
    LabelTextInput,
    NumberInput,
    DurationText,ToggleInput
  },
  computed: {
    ...mapState("tracks", ["isLoadingTrack", "currentTrack"]),
    currentStationUid() {
      return this.$store.state.currentStationUid;
    },
    isDisable() {
      return this.viewState.isUpdatingTrack || !(this.currentTrack.artists.length) > 0 || !this.currentTrack.title
    },
    maxYear() {
      new Date().getFullYear();
    },
    getLength() {
      const length = parseInt(this.currentTrack.duration);
      const min = Math.floor(length / 60000);
      const sec = Math.floor((length % 60000) / 1000);
      const ms = (length % 60000).toString().substr(-3);

      return `${min}:${sec}:${ms}`;
    },
    fileType() {
      var id = this.currentTrack.typeID;
      if (id === 1 || id === 2 || id === 3 || id === 4 || id === 5 || id === 9 ){
        return Object.values(Constants.trackTypes).find(type => type.value === this.currentTrack.typeID).key
      }else{
        return Object.values(Constants.trackTypes).find(type => type.key === this.currentTrack.typeID).key
      }

    }
  },
  mounted() {
    this.$store.dispatch("tracks/loadTrack", {
      stationUid: this.currentStationUid,
      trackUid: this.$route.params.trackUid
    });
  },
  watch: {
    "$route.params": function(to, from) {
      this.$store.dispatch("tracks/loadTrack", {
        stationUid: this.currentStationUid,
        trackUid: this.$route.params.trackUid
      });
    }
  },
  data() {
    return {
      viewState: {
        fieldFocused: null,
        isLoadingTracks: false,
        isRemovingTracks: false,
        isUpdatingTrack: false,
        showArtistSelector: false,
        showInstrumentSelector: false,
        showVocalInstrumentSelector: false,
        showLanguageSelector: false,
        showStyleSelector: false,
        showLabelSelector: false,
        showTagsSelector: false,
        showDeleteConfirmation: false
      },
      trackResponse: null,
      Constants,
      errors: []
    };
  },
  methods: {
    ...mapActions("tracks", ["loadTrack"]),
    getError(field) {
      return this.errors.indexOf(field) >= 0 || this.errors.indexOf(field.toLowerCase()) >= 0;
    },
    onFocus(field) {
      console.log(field)
      this.$set(this.viewState, "fieldFocused", field);
    },
    onBlur() {
      this.$set(this.viewState, "fieldFocused", null);
    },
    onNewValue(field, value) {
      console.log(field, value)
      this.currentTrack[field] = value;
      console.log(this.currentTrack)

    },
    onClose() {
      this.$router.push(this.$store.state.tracks.prevPage);
    },
    onNext() {
      if (
        this.$store.state.tracks.tracksBucketIndex ===
        this.$store.state.tracks.tracksBucket.length - 1
      ) {
        this.onClose();
        return;
      }

      this.$store.commit("tracks/next");
      this.$router.push({
        name: "TrackHome",
        params: {
          stationUid: this.$store.state.currentStationUid,
          trackUid: this.$store.state.tracks.tracksBucket[
            this.$store.state.tracks.tracksBucketIndex
          ]
        }
      });
      // this.$el.parentElement.scrollTop = 0;
    },
    onPrev() {
      if (this.$store.state.tracks.tracksBucketIndex === 0) {
        this.onClose();
        return;
      }

      this.$store.commit("tracks/prev");
      this.$router.go(-1);
    },
    resetModalState(toggler) {
      this.$set(this.viewState, toggler, false);
    },
    updateTrackField(tracksToUpdate, field, value) {
      if (['mood', 'energy', 'genderId'].includes(field) && this.currentTrack[field] === value) {
        this.currentTrack[field] = 0
      } else {
        console.log(`tracksToUpdate: ${tracksToUpdate} | field: ${field} | value: ${value}`);
        this.currentTrack[field] = value;
      }
    },
    openParameterSelector(toggler, selectorName, tracks) {
      if (!tracks || tracks.length === 0) return;
      this.viewState[toggler] = true;

      try {
        var modalSelector = this.$refs[selectorName];

        if (modalSelector) {
          modalSelector.editForTracks(tracks);
        }
      } catch (e) {
        console.log("exc:", e);
      }
    },
    fetchErrorsFromResponse(errors) {
      this.errors = []

      errors.forEach(error => {
        error.fieldNames.forEach(field => {
          this.errors.push(field.toLowerCase())
        })
      })
    },
    goToError() {
      const { $el } = this;
      const elRect = $el.getBoundingClientRect();
      const $error = $el.querySelector(".detail-cell.has-error");
      const rect = $error.getBoundingClientRect();

      const to = rect.top - elRect.top - 30;

      this.$el.parentElement.scrollTop = to;
    },
    formatTrack() {
      const track = { ...this.currentTrack };
      console.log('track: ', track);
      track.languages = !!track.languages === false ? null : track.languages.map(lang => lang.code);
      track.styles = track.styles && track.styles.map(style => {
        return { styleId: style.id };
      });
      track.tags = track.tags && track.tags.map(tag => tag.id);

      if (['MUSIC'].includes(this.fileType)) {
        track.artists = track.artists && track.artists.map(art => {
          return {artistId: art.id, isVisible: art.isVisible};
        });
      } else if (['JINGLE', 'PODCAST'].includes(this.fileType)) {
        track.artists = null
      }

      // label can be array or object, extract right value
      if( Array.isArray(track.label) ){
        track.labelID = track.label[0].id
      } else {
        track.labelID = track.label ? track.label.id : null;
      }

      return track;
    },
    async onSave() {
      this.errors = []

      this.viewState.isUpdatingTrack = true;

      try {
        const result = await this.updateTrack();
        this.viewState.isUpdatingTrack = false;

        if (this.$store.state.tracks.closeAfterEdit) {
          this.onNext();
        }
      } catch ({ response }) {
        this.viewState.isUpdatingTrack = false;
        if (response.data.errors.length) {
          this.fetchErrorsFromResponse(response.data.errors)
          setTimeout(() => {
            this.goToError();
          }, 100);
          return
        }
      }
    },
    async updateTrack() {
      const track = this.formatTrack();
      console.log('updateTrack');

      const response =  await HTTP.post(Apis.library.tracks.update.default, {
          keys: [track.uid],
          stationUid: this.currentStationUid,
          value: track
        });
      if(response.status === 200){
        await this.$store.dispatch('Toasts/addToast', {
          title: 'Success',
          message: 'Updated successfully',
          type: 'success'
        });
      }
    }
  }
};
</script>



<style lang="scss" scoped>
@import "../../../assets/css/colors";

.detail {
  padding: 10px 25px 40px;
}

.detail-wrapper {
  display: flex;
  flex-direction: column;
  max-width: 100vw;
}

.detail-title {
  color: #292c33;
  font-size: 16px;
  line-height: 1;

  &:not(:first-child) {
    margin-top: 40px;
  }

  + .detail-row {
    margin-top: 20px;
  }
}

.detail-row {
  display: flex;
  margin: 40px -15px 0;

  &:first-child {
    margin-top: 0;
  }

  &--2cols {
    .detail-cell {
      width: 50%;
    }
  }

  &--3cols {
    .detail-cell {
      width: 33.33%;

      &--big {
        width: 66.66%;
      }
    }
  }
}

.detail-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 0 15px;

  &.has-error {
    .detail-error {
      display: block;
    }
  }
}

.detail-label {
  display: block;
  transition: transform 0.3s ease-in-out;
  color: #696b70;
  font-size: 13px;
  font-weight: 400;
  letter-spacing: 0.2px;
  line-height: calc(16 / 13);

  i {
    margin-left: 5px;
    color: #abacaf;
    font-style: italic;
  }
}

.detail-value {
  margin-top: 8px;
  display: block;
  color: #292c33;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.2px;
  line-height: calc(17 / 14);
}

.detail-error {
  display: none;
  margin-top: 8px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1;
  color: #f00;
}

// Footer
// -----------------------------------------------------------------------------
.detail-footer {
  display: flex;
  justify-content: space-between;
  flex-shrink: 0;
  position: relative;
  width: 100%;
  margin-top: 20px;
  padding-top: 25px;
  border-top: 1px solid #d2d2d2;

  button {
    text-transform: uppercase;
  }
}

.detail-footer-right {
  display: flex;
  align-items: center;
  margin-left: auto;

  .m-loader + .detail-button {
    opacity: 0.5;
    pointer-events: none;
  }

  .detail-button {
    margin-left: 20px;
  }
}

.detail-footer-loader {
  color: #696b70;
  font-size: 14px;
  margin-right: 10px;
}

.detail-link {
  padding: 0;
  border: 0;
  border-radius: 0;
  transition: color 0.2s ease-in-out;
  background-color: transparent;
  color: #abacaf;
}

// Transitions
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.textarea{
  width: 100%;
  resize: none;
  color: #000000;
  padding: 5px;
  border: 1px solid #d2d2d2;
}
</style>

<style lang="scss">
.detail {
  .detail-row {
    .detail-cell {
      input[type="number"],
      input[type="text"] {
        width: 100%;
        border-color: #d2d2d2;
        font-size: 14px;
        line-height: calc(17 / 14);
      }

      &.has-error {
        input[type="number"],
        input[type="text"] {
          border-color: red;
        }
      }
    }
  }
}
</style>
